'use client';

import { useEffect, useRef } from 'react';

import { mountGiscus, readGiscusConfig } from '@/integrations/giscus';

type WritingDiscussionProps = {
  enabled: boolean;
};

export function WritingDiscussion({ enabled }: WritingDiscussionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const config = readGiscusConfig();

  useEffect(() => {
    if (!enabled || !config || !containerRef.current) {
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const idleCallback = (
      globalThis as typeof globalThis & {
        requestIdleCallback?: (
          callback: () => void,
          options?: { timeout: number }
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      }
    ).requestIdleCallback;

    const start = () => {
      if (cancelled || !containerRef.current) return;
      try {
        cleanup = mountGiscus(containerRef.current, config);
      } catch (error) {
        console.warn('Giscus failed to mount', error);
      }
    };

    if (idleCallback) {
      const id = idleCallback(start, { timeout: 2000 });
      return () => {
        cancelled = true;
        (
          globalThis as typeof globalThis & {
            cancelIdleCallback?: (handle: number) => void;
          }
        ).cancelIdleCallback?.(id);
        cleanup?.();
      };
    }

    const timeoutId = window.setTimeout(start, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      cleanup?.();
    };
  }, [enabled, config]);

  if (!enabled) {
    return null;
  }

  if (!config) {
    return (
      <div className="border-border bg-muted/30 min-h-[120px] rounded-xl border p-6 text-center">
        <p className="text-muted-foreground font-mono text-sm">
          Discussion is unavailable; Giscus is not configured for this build.
        </p>
      </div>
    );
  }

  return <div ref={containerRef} className="min-h-[120px]" />;
}
